import React from 'react';
import { useTranslation } from 'react-i18next';
import useUniqueId from 'hooks/useUniqueId';
import Flex from 'components/Flex';
import ProgressCircle from 'components/ProgressCircle';
import VisuallyHidden from 'components/VisuallyHidden';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function useProgressLabel(_ref) {
  var progress = _ref.progress,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'default' : _ref$type,
    isContentLocked = _ref.isContentLocked;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var status = progress.status,
    value = progress.value;
  if (isContentLocked) {
    return t('section_locked_status_locked');
  }
  if (status === 'notstarted' && !value) {
    return t('actions_not_started');
  }
  // This is a special case for playlists, as they don't have an incomplete status
  if (status === 'opened' && !value) {
    return t('actions_opened');
  }
  if (status === 'incomplete' || status === 'opened' && value > 0 ||
  // A playlist can not have been opened yet, but contain completed assets
  status === 'notstarted' && value > 0) {
    var percentage = "".concat(Math.round(value * 100), "%");
    if (type === 'short') {
      return percentage;
    } else {
      return t('actions_done_percentage', {
        percentage: percentage
      });
    }
  }
  if (status === 'failed') {
    return t('actions_failed');
  }
  if (status === 'completed' || status === 'passed') {
    return t('actions_done');
  }
  return t('property_status');
}
function getContentStatusIconAndColor(status) {
  switch (status) {
    case 'failed':
      return {
        icon: 'x',
        color: 'negative'
      };
    case 'completed':
    case 'passed':
      return {
        icon: 'ok',
        color: 'positive'
      };
    default:
      return {
        color: 'positive'
      };
  }
}
function ContentProgressCircle(_ref2) {
  var progress = _ref2.progress,
    _ref2$isContentLocked = _ref2.isContentLocked,
    isContentLocked = _ref2$isContentLocked === void 0 ? false : _ref2$isContentLocked,
    _ref2$scale = _ref2.scale,
    scale = _ref2$scale === void 0 ? 1 : _ref2$scale,
    contentName = _ref2.contentName,
    _ref2$infoReadout = _ref2.infoReadout,
    infoReadout = _ref2$infoReadout === void 0 ? 'hidden' : _ref2$infoReadout;
  var _getContentStatusIcon = getContentStatusIconAndColor(progress.status),
    icon = _getContentStatusIcon.icon,
    color = _getContentStatusIcon.color;
  var infoText = useProgressLabel({
    progress: progress,
    type: infoReadout === 'short' ? 'short' : 'default',
    isContentLocked: isContentLocked
  });
  var infoTextId = useUniqueId();
  var circle = /*#__PURE__*/_jsx(ProgressCircle, {
    color: color,
    dimmed: isContentLocked || progress.status === 'notstarted',
    value: isContentLocked ? 0 : progress.value,
    a11yLabel: contentName,
    iconWhenFull: icon,
    iconWhenEmpty: isContentLocked ? 'lock' : undefined,
    scale: scale,
    tooltip: infoReadout === 'tooltip' ? infoText : false,
    "aria-describedby": infoTextId
  });
  if (infoReadout === 'full' || infoReadout === 'short') {
    return /*#__PURE__*/_jsxs(Flex, {
      display: "inline-flex",
      gap: "xxs",
      verticalAlign: "top",
      children: [circle, /*#__PURE__*/_jsx("span", {
        id: infoTextId,
        children: infoText
      })]
    });
  }
  return /*#__PURE__*/_jsxs(Flex, {
    display: "inline-block",
    verticalAlign: "top",
    children: [circle, /*#__PURE__*/_jsx(VisuallyHidden, {
      id: infoTextId,
      children: infoText
    })]
  });
}
export default ContentProgressCircle;